import { ChangeEvent, useEffect, useRef, useState } from 'react';
import { Cropper } from '@components/cropper';
import { FILE_TYPE_AVIF, FILE_TYPE_JPEG, FILE_TYPE_JPG, FILE_TYPE_PNG, FILE_TYPE_WEBP } from '@constants/global';
import {
  closestCenter,
  DndContext,
  DragEndEvent,
  DragOverlay,
  DragStartEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { restrictToParentElement } from '@dnd-kit/modifiers';
import { arrayMove, rectSortingStrategy, SortableContext } from '@dnd-kit/sortable';
import { isFileFormatCorrect } from '@utils/is-file-format-correct';

import { ImageGalleryAddTile } from './components/image-gallery-add-tile';
import { ImageGalleryEmpty } from './components/image-gallery-empty';
import { ImageGallerySkeletonTile } from './components/image-gallery-skeleton-tile';
import { ImageGalleryThumbnail } from './components/image-gallery-thumbnail';
import { ImageGalleryThumbnailImage } from './components/image-gallery-thumbnail-image';
import { isFileSizeValid } from './utils/validate-file-size';
import { ImageGalleryProps } from './image-gallery.type';

const DEFAULT_MAX_FILE_SIZE_MB = 12;
const DEFAULT_ACCEPTED_TYPES = [FILE_TYPE_JPG, FILE_TYPE_JPEG, FILE_TYPE_PNG, FILE_TYPE_WEBP, FILE_TYPE_AVIF];

const buildAcceptAttribute = (types: string[]) => types.map((ext) => `.${ext}`).join(',');

export const ImageGallery = ({
  images,
  maxImages,
  maxFileSizeMB = DEFAULT_MAX_FILE_SIZE_MB,
  acceptedTypes = DEFAULT_ACCEPTED_TYPES,
  onUpload,
  onReorder,
  onDelete,
  onFileTooLarge,
  onFileTypeRejected,
  isUploading = false,
  deletingId = null,
  emptyDropzoneTitle,
  emptyDropzoneSubtitle,
  uploadButtonAriaLabel,
  deleteButtonAriaLabel,
  maxReachedMessage,
  cropConfig,
}: ImageGalleryProps) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const pendingCropUrlRef = useRef<string | null>(null);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [pendingCropFile, setPendingCropFile] = useState<{ file: File; src: string } | null>(null);
  const sensors = useSensors(useSensor(PointerSensor), useSensor(KeyboardSensor));

  const openFilePicker = () => inputRef.current?.click();

  const clearPendingCropFile = () => {
    if (pendingCropUrlRef.current) {
      URL.revokeObjectURL(pendingCropUrlRef.current);
      pendingCropUrlRef.current = null;
    }
    setPendingCropFile(null);
  };

  const handleFile = (file: File) => {
    if (!isFileFormatCorrect({ type: file.type, allowedTypes: acceptedTypes })) {
      onFileTypeRejected?.(file);
      return;
    }
    if (!isFileSizeValid(file, maxFileSizeMB)) {
      onFileTooLarge?.(file);
      return;
    }
    if (cropConfig) {
      if (pendingCropUrlRef.current) URL.revokeObjectURL(pendingCropUrlRef.current);
      const src = URL.createObjectURL(file);
      pendingCropUrlRef.current = src;
      setPendingCropFile({ file, src });
      return;
    }
    onUpload(file);
  };

  const handleCropSave = (croppedFile: File) => {
    clearPendingCropFile();
    onUpload(croppedFile);
  };

  const handleCropCancel = () => {
    clearPendingCropFile();
  };

  useEffect(
    () => () => {
      if (pendingCropUrlRef.current) URL.revokeObjectURL(pendingCropUrlRef.current);
    },
    []
  );

  const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) handleFile(file);
    // Reset so the same file can be picked twice in a row
    e.target.value = '';
  };

  const handleDragStart = (event: DragStartEvent) => {
    setActiveId(String(event.active.id));
  };

  const handleDragEnd = (event: DragEndEvent) => {
    setActiveId(null);
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = images.findIndex((img) => img.id === active.id);
    const newIndex = images.findIndex((img) => img.id === over.id);
    if (oldIndex === -1 || newIndex === -1) return;

    const reordered = arrayMove(images, oldIndex, newIndex);
    onReorder(reordered.map((img) => img.id));
  };

  const handleDragCancel = () => {
    setActiveId(null);
  };

  const activeImage = activeId ? images.find((img) => img.id === activeId) : null;

  const isEmpty = images.length === 0;
  const canAddMore = images.length < maxImages && !isUploading;
  const isMaxReached = !isEmpty && images.length >= maxImages;

  return (
    <>
      <input
        ref={inputRef}
        type='file'
        accept={buildAcceptAttribute(acceptedTypes)}
        multiple={false}
        className='hidden'
        onChange={handleInputChange}
        data-testid='image-gallery__file-input'
      />

      {isEmpty ? (
        <ImageGalleryEmpty
          title={emptyDropzoneTitle}
          subtitle={emptyDropzoneSubtitle}
          onClick={openFilePicker}
          onFileDropped={handleFile}
          isUploading={isUploading}
        />
      ) : (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          modifiers={[restrictToParentElement]}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
          onDragCancel={handleDragCancel}>
          <SortableContext items={images.map((img) => img.id)} strategy={rectSortingStrategy}>
            <div
              className='w-full rounded-sm border border-solid border-soft fill-primary py-3 px-4 flex flex-row flex-wrap gap-2.5 transition-colors'
              data-testid='image-gallery__list'>
              {images.map((image) => (
                <ImageGalleryThumbnail
                  key={image.id}
                  image={image}
                  onDelete={onDelete}
                  deleteButtonAriaLabel={deleteButtonAriaLabel}
                  isDeleting={deletingId === image.id}
                />
              ))}
              {isUploading && <ImageGallerySkeletonTile />}
              {canAddMore && <ImageGalleryAddTile onClick={openFilePicker} ariaLabel={uploadButtonAriaLabel} />}
            </div>
          </SortableContext>
          <DragOverlay dropAnimation={{ duration: 250, easing: 'cubic-bezier(0.16, 1, 0.3, 1)' }}>
            {activeImage ? (
              <div className='w-[60px] h-[60px] shadow-lg rotate-2 scale-105 cursor-grabbing'>
                {/* The drag overlay only mounts while a thumb is being dragged,
                    which can only happen after that thumb has loaded — so we
                    short-circuit the load tracking and render fully visible. */}
                <ImageGalleryThumbnailImage
                  image={activeImage}
                  isLoaded
                  showSkeleton={false}
                  onLoad={() => undefined}
                  onError={() => undefined}
                />
              </div>
            ) : null}
          </DragOverlay>
        </DndContext>
      )}

      {isMaxReached && maxReachedMessage && (
        <p data-testid='image-gallery__max-reached' className='mt-2 text-body-xs text-critical'>
          {maxReachedMessage}
        </p>
      )}

      {cropConfig && pendingCropFile && (
        <Cropper
          open
          image={pendingCropFile}
          aspectRatio={cropConfig.aspectRatio}
          translations={cropConfig.translations}
          onSave={handleCropSave}
          onCancel={handleCropCancel}
        />
      )}
    </>
  );
};
